/**
 * Cart Upsell - CSS Scroll-Snap Slider with Server Rendering
 * Replaces Flickity with native CSS scroll-snap
 * Updates via Section Rendering API on cart changes
 * Includes GA4 tracking for impressions, clicks, and conversions
 */

if (!customElements.get('cart-upsell')) {
  customElements.define(
    'cart-upsell',
    class CartUpsell extends HTMLElement {
      constructor() {
        super();
        this.unsubscriber = null;
        this.impressionTracked = false;
        this.intersectionObserver = null;
      }

      connectedCallback() {
        // Setup scroll navigation
        this.setupNavigation();

        // Setup add to cart button handlers
        this.addEventListener('click', this.onClick.bind(this));

        // Setup impression tracking
        this.setupImpressionTracking();

        // Subscribe to cart updates
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            this.onCartUpdate(event);
          });
        }
      }

      disconnectedCallback() {
        if (this.unsubscriber) {
          this.unsubscriber();
        }
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }

      // ========== GA4 TRACKING ==========

      /**
       * Setup IntersectionObserver to track when upsell section is viewed
       */
      setupImpressionTracking() {
        if (typeof IntersectionObserver === 'undefined') return;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.impressionTracked) {
                this.impressionTracked = true;
                this.trackImpression();
              }
            });
          },
          { threshold: 0.5 }, // Track when 50% visible
        );

        this.intersectionObserver.observe(this);
      }

      /**
       * Get product data from all visible upsell items
       */
      getUpsellProductsData() {
        const buttons = this.querySelectorAll('button[data-variant-id]');
        const items = [];

        buttons.forEach((button, index) => {
          items.push({
            item_id: button.dataset.productId || '',
            item_name: button.dataset.productTitle || '',
            item_variant: button.dataset.productVariant || '',
            price: parseFloat(button.dataset.productPrice) || 0,
            index: index,
            item_list_id: 'cart_upsell',
            item_list_name: 'Cart Upsell',
          });
        });

        return items;
      }

      /**
       * Get product data from a specific button
       */
      getProductDataFromButton(button) {
        return {
          item_id: button.dataset.productId || '',
          item_name: button.dataset.productTitle || '',
          item_variant: button.dataset.productVariant || '',
          price: parseFloat(button.dataset.productPrice) || 0,
          quantity: 1,
          item_list_id: 'cart_upsell',
          item_list_name: 'Cart Upsell',
        };
      }

      /**
       * Track upsell section impression (view_item_list)
       */
      trackImpression() {
        if (typeof gtag !== 'function') return;

        const items = this.getUpsellProductsData();
        if (items.length === 0) return;

        gtag('event', 'upsell_view_item_list', {
          item_list_id: 'cart_upsell',
          item_list_name: 'Cart Upsell',
          items: items,
        });
      }

      /**
       * Track add button click (select_item)
       */
      trackClick(button) {
        if (typeof gtag !== 'function') return;

        const item = this.getProductDataFromButton(button);

        gtag('event', 'upsell_select_item', {
          item_list_id: 'cart_upsell',
          item_list_name: 'Cart Upsell',
          items: [item],
        });
      }

      /**
       * Track successful add to cart
       */
      trackAddToCartSuccess(button, cartData) {
        if (typeof gtag !== 'function') return;

        const item = this.getProductDataFromButton(button);

        gtag('event', 'upsell_add_to_cart', {
          currency: window.Shopify?.currency?.active || 'EGP',
          value: item.price,
          items: [item],
          upsell_source: 'cart_drawer',
        });
      }

      /**
       * Track failed add to cart
       */
      trackAddToCartFail(button, errorMessage) {
        if (typeof gtag !== 'function') return;

        const item = this.getProductDataFromButton(button);

        gtag('event', 'upsell_add_to_cart_failed', {
          item_id: item.item_id,
          item_name: item.item_name,
          error_message: errorMessage || 'Unknown error',
          upsell_source: 'cart_drawer',
        });
      }

      // ========== END GA4 TRACKING ==========

      setupNavigation() {
        const container = this.querySelector('.upsell-slider__container');
        const prevBtn = this.querySelector('.upsell-nav__prev');
        const nextBtn = this.querySelector('.upsell-nav__next');

        if (!container) return;

        // Calculate scroll amount (80% of container width)
        const getScrollAmount = () => container.offsetWidth * 0.8;

        // Scroll handlers
        if (prevBtn) {
          prevBtn.addEventListener('click', () => {
            container.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', () => {
            container.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
          });
        }

        // Update button states based on scroll position
        const updateNavButtons = () => {
          if (!prevBtn || !nextBtn) return;

          const { scrollLeft, scrollWidth, clientWidth } = container;
          const isRTL = document.dir === 'rtl';

          if (isRTL) {
            // RTL: scrollLeft is negative or positive depending on browser
            const maxScroll = scrollWidth - clientWidth;
            prevBtn.disabled = Math.abs(scrollLeft) >= maxScroll - 5;
            nextBtn.disabled = Math.abs(scrollLeft) <= 5;
          } else {
            prevBtn.disabled = scrollLeft <= 5;
            nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 5;
          }
        };

        // Listen for scroll events (debounced)
        let scrollTimeout;
        container.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(updateNavButtons, 50);
        });

        // Initial state
        updateNavButtons();

        // Update on resize
        if (typeof ResizeObserver !== 'undefined') {
          const resizeObserver = new ResizeObserver(() => {
            updateNavButtons();
          });
          resizeObserver.observe(container);
        }
      }

      onClick(event) {
        const button = event.target.closest('button[data-variant-id]');
        if (!button || button.hasAttribute('disabled')) return;

        event.preventDefault();

        // Track click event
        this.trackClick(button);

        this.addToCart(button.dataset.variantId, button);
      }

      async addToCart(variantId, button) {
        // Store original state
        const originalText = button.textContent;
        button.setAttribute('disabled', 'true');

        // Show spinner inside button
        button.innerHTML = `
          <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="16" height="16">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        `;

        try {
          // Add to cart
          const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: [{ id: parseInt(variantId), quantity: 1 }],
            }),
          });

          const addData = await addResponse.json();

          // Check for Shopify errors (e.g., out of stock)
          if (addData.status) {
            console.error('Add to cart error:', addData.description);
            this.trackAddToCartFail(button, addData.description);
            button.removeAttribute('disabled');
            button.textContent = 'Error';
            setTimeout(() => {
              button.textContent = originalText;
            }, 2000);
            return;
          }

          // Fetch updated sections via Section Rendering API
          const sectionsToFetch = ['cart-drawer', 'cart-upsell', 'free-shipping-bar', 'cart-icon-bubble'].join(',');

          const sectionsResponse = await fetch(`${window.Shopify.routes.root}?sections=${sectionsToFetch}`);
          const sections = await sectionsResponse.json();

          // Update this component directly (since onCartUpdate skips self-triggered events)
          const upsellSectionId = this.dataset.sectionId || 'cart-upsell';
          if (sections[upsellSectionId]) {
            this.updateFromHTML(sections[upsellSectionId]);
          }

          // Publish cart update event with all section HTML
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-upsell',
              sections: sections,
              cartData: addData,
            });
          }

          // Track successful add to cart
          this.trackAddToCartSuccess(button, addData);

          // Success feedback
          button.textContent = 'Added';
          setTimeout(() => {
            button.removeAttribute('disabled');
            button.textContent = 'ADD';
          }, 1500);
        } catch (error) {
          console.error('Error adding to cart:', error);
          this.trackAddToCartFail(button, error.message || 'Network error');
          button.removeAttribute('disabled');
          button.textContent = 'Error';
          setTimeout(() => {
            button.textContent = originalText;
          }, 2000);
        }
      }

      onCartUpdate(event) {
        // Skip if this component triggered the update
        if (event.source === 'cart-upsell') return;

        // Extract section HTML from event data
        const sectionId = this.dataset.sectionId || 'cart-upsell';

        if (event.sections && event.sections[sectionId]) {
          this.updateFromHTML(event.sections[sectionId]);
        }
      }

      updateFromHTML(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector('cart-upsell');

        if (newContent) {
          this.innerHTML = newContent.innerHTML;
          // Re-setup navigation after HTML replacement
          this.setupNavigation();
          // Reset impression tracking for new products
          this.impressionTracked = false;
        }
      }
    },
  );
}
